import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import { withRouter } from "react-router-dom";
import axios from 'axios';
import Select from 'react-select';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { Container, Row, Col } from 'reactstrap';
import UrlUtils from '../../../services/utils/UrlUtils';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import DamageAnalytics from './Charts/DamageAnalytics';
import RevenueByLocations from './RevenueByLocations';
import LatestTransactions from './LatestTransactions';
import CategoriesAnalytics from './CategoriesAnalytics';
import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper';
import { onGetInfoOrganizationSuccess } from '../../../store/organization/actions';
import { errorNotification, loginUserSuccessful, apiError, appLoadStatusChanged, changePreloader } from '../../../store/actions';
import { changeWorkspace } from '../../../store/workspace/actions';
import {
	API_URL_ADMIN_DASHBOARD_PARTNER_LIST,
	API_URL_GET_USER_DETAIL,
	API_URL_VERIFY_ORGANIZATION,
	CM_API_URL_ADMIN_DASHBOARD_PARTNER_LIST,
	CM_API_URL_GET_USER_DETAIL,
} from '../../../common/constants';
import DownloadStatistics from './components/DownloadStatistics';
import Filtering from './components/Filtering';
import DashboardService from 'src/apis/DashboardService';
import DashboardTotalCases from "./components/DashboardSummarySection/TotalCases";
import DashboardChannel from "./components/DashboardSummarySection/Channels";
import DashboardRiskRatings from "./components/DashboardSummarySection/RiskRatings";
import DashboardCaseTypes from "./components/DashboardSummarySection/CaseTypes";
import DashboardInvestigationTime from "./components/DashboardSummarySection/InvestigationTime";
import DashboardAssignedToMe from "./components/DashboardSummarySection/AssignedToMe";
import DashboardHeatmap from './components/DashboardHeatmap';
import DashboardCategories from './components/DashboardCategories';
import DashboardCasesByDate from './components/DashboardCasesByDate';
import DashboardLatesCases from './components/DashboardLatesCases';

const Dashboard = ({
    t,
    modules,
    user,
    token
}) => {
    const urlUtils = new UrlUtils();
    const userRoles = user?.roles || [];

    const [ filters, setFilters ] = useState({});
    const [ selectedClient, setSelectedClient ] = useState();
    const [ userRoleLoaded, setUserRoleLoaded ] = useState(false);

    const handleFilterOnChange = useCallback((newFilters) => {
        setFilters((currentState) => ({...currentState, ...newFilters}));
    }, []);
  
    const {
        isFetching: fetchingPartners,
        data: partnersList,
        refetch: refetchPartnersQuery
    } = useQuery({
        queryKey: [
            "fetch-partners-query",
            user?.id
        ],
        queryFn: async () => {
            const url = modules.active === 'wb' ? API_URL_ADMIN_DASHBOARD_PARTNER_LIST : CM_API_URL_ADMIN_DASHBOARD_PARTNER_LIST;

            const response = await axios.get(url, {
                headers: {
					Authorization: `Bearer ${token}`,
				}
            })

            return response.data?.data || [];
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: false,
        onError: () => {
            toast(t("An error occurred while fetching organizations list."), {
                type: "error"
            });
        }
    });

    const {
        mutate: organizationVerifyMutation,
        isLoading: orgVerificationIsLoading
    } = useMutation({
        mutationFn: async ({
            orgId,
            orgDomain
        }) => {
            return await axios.post(
                API_URL_VERIFY_ORGANIZATION,
                {
                    id: orgId,
                    url: decodeURI(orgDomain),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
        },
        onSuccess: (_response, {
            orgId,
            orgDomain,
            orgName
        }) => {
            localStorage.setItem('org', urlUtils.getAppRootPath(orgId, orgName));
            const newBaseUrl = urlUtils.getAppRootPath(orgId, decodeURI(orgDomain));
            window.location.replace(`${newBaseUrl}/${window.location.pathname.split('/', 4)[3]}/dashboard`);
        },
        onError: () => {
            toast(t("Failed to verify organization. please try again."), {
                type: "error"
            });
        },
    });

    const {
        mutate: userVerificationMutation,
        isLoading: userVerificationIsLoading
    } = useMutation({
        mutationFn: async ({
            orgId
        }) => {
            const url = modules.active === 'wb' ? API_URL_GET_USER_DETAIL : CM_API_URL_GET_USER_DETAIL;

            return await axios.post(
                url,
                {
                    organizationId: orgId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
        },
        onSuccess: (_response, orgInfo) => {
            organizationVerifyMutation(orgInfo)
        },
        onError: () => {
            toast(t("Failed to verify organization. please try again."), {
                type: "error"
            });
        }
    });

    const breadcrumbItems = useMemo(() => {
        return [
            { title: 'SIP', link: '/' },
            { title: 'Dashboard', link: '#' }
        ]
    }, []);

    const clients = useMemo(() => {
        let options = [];
        if(user && user.roles.includes('ROLE_PARTNER')){
            options = [
                {
                    label: t('All'),
                    value: 'all',
                    extra: {
                        id: user.organization.id,
                        name: user.organization.name,
                        code: user.organization.country_code,
                        domain: user.orgUrl
                    }
                },
                {
                    label: t('My own company'),
                    value: 'own',
                    extra: {
                        id: user.organization.id,
                        name: user.organization.name,
                        code: user.organization.country_code,
                        domain: user.orgUrl
                    }
                },
                ...(partnersList || []).map((par) => ({
                    value: par.id,
                    label: par.name,
                    extra: {
                        id: par.id,
                        name: par.name,
                        code: par.code,
                        domain: par.domain
                    }
                }))
            ];

            let orgToSearch = localStorage.getItem('filterBy') || user.organization.id || urlUtils.getOrgId();

            setSelectedClient(
                options.find((option) => option.value.toString() === orgToSearch.toString())
            );
        }

        return options;
    }, [
        t,
        user,
        partnersList
    ]);

    // const categories = useMemo(() => {
    //     const result = [];
    //     const categories = dashboardData?.categories || {};

    //     for (const item in categories) {
    //         result.push({
    //             amount: categories[item].amount,
    //             title: t(categories[item].title),
    //             percent: categories[item].percent,
    //         });
    //     }

    //     return result;
    // }, [
    //     dashboardData?.categories,
    //     t
    // ]);

    useEffect(() => {
        if(user){
            let filterPartner;
            if(user.roles.includes('ROLE_PARTNER')){ 
                if(localStorage.getItem('filterBy') === 'all'){
                    filterPartner = localStorage.getItem('filterBy')
                }
                refetchPartnersQuery();  
            }       

            setFilters((currentState) => (
                {
                    ...currentState, 
                    partner: filterPartner
                }
            ));

            setTimeout(() => {
                setUserRoleLoaded(true);
            }, 250);
        }
    }, [
        user
    ]);

    useEffect(() => {
        if(selectedClient){
            const orgInStorage = localStorage.getItem('filterBy');

            if(orgInStorage !== selectedClient.value.toString()){
                localStorage.setItem('filterBy', selectedClient.value);

                userVerificationMutation({
                    orgId: selectedClient.extra.id,
                    orgDomain: selectedClient.extra.domain,
                    orgName: selectedClient.extra.name.replace(/\s+/g, '-').toLowerCase()
                });
            }
        }
    }, [ selectedClient ]);

    
    return (
        <div className='page-content'>
            <Container fluid>
                <Breadcrumbs 
                    title='Dashboard' 
                    breadcrumbItems={breadcrumbItems} 
                />

                <Row className='mb-4 mt-4'>
                    <Col sm='12' md='6' lg='4'>
                        {userRoles.includes('ROLE_PARTNER') && (
                            <div style={{
                                maxWidth: '220px'
                            }}>
                                <Select
                                    isDisabled={fetchingPartners || userVerificationIsLoading || orgVerificationIsLoading}
                                    isLoading={fetchingPartners || userVerificationIsLoading || orgVerificationIsLoading}
                                    name='sortByOrganization'
                                    classNamePrefix='select2-selection'
                                    options={clients}
                                    onChange={(e) => {
                                        setSelectedClient(e)
                                    }}
                                    placeholder={t('Filter by organization')}
                                    value={selectedClient}
                                />
                            </div>
                        )}
                    </Col>

                    <Col sm='12' md='6' lg='8' className='d-flex justify-content-end align-items-start'>
                        <Filtering 
                            onChange={handleFilterOnChange}
                        />

                        {isFeatureEnabledForActiveModule('dashboard-download-statistics-button') && (
                            <DownloadStatistics 
                                filters={filters} 
                            />
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col xl={8}>
                        {isFeatureEnabledForActiveModule('total-cases') && (
                            <DashboardTotalCases filters={filters} modules={modules} />
                        )} 

                        {isFeatureEnabledForActiveModule('amount-of-cases') && (
                            <DashboardCasesByDate filters={filters} modules={modules} />
                        )}
                        
                        <Row style={{ alignContent: 'stretch' }} className='row-equal-height'>
                            {isFeatureEnabledForActiveModule('channels') && (
                                <Col lg={6} style={{ height: '100%' }}>
                                    <DashboardChannel filters={filters} modules={modules} />
                                </Col>
                            )}
                            {isFeatureEnabledForActiveModule('risk-rate') && (
                                <Col lg={6} style={{ height: '100%' }}>
                                    <DashboardRiskRatings filters={filters} modules={modules} />
                                </Col>
                            )}
                        </Row>

                        {isFeatureEnabledForActiveModule('heatmap') && (
                            <DashboardHeatmap filters={filters} modules={modules} />
                        )}
                    </Col>
                    <Col xl={4}>
                        {isFeatureEnabledForActiveModule('top5category') && (
                            <DashboardCategories filters={filters} modules={modules} />
                        )}

                        {isFeatureEnabledForActiveModule('case-type') && modules.active !== 'cm' && (
                            <DashboardCaseTypes filters={filters} modules={modules} />
                        )}

                        {isFeatureEnabledForActiveModule('processing-time') && (
                            <DashboardInvestigationTime filters={filters} modules={modules} />
                        )}

                        {isFeatureEnabledForActiveModule('cases-assigned-to-me') && (
                            <DashboardAssignedToMe filters={filters} modules={modules} />
                        )} 
                    </Col>
                </Row>

                {isFeatureEnabledForActiveModule('lateast-cases') && (
                    <DashboardLatesCases filters={filters} modules={modules} orgIsAll={selectedClient?.value === 'all'}  />
                )}
            </Container>
        </div>
    )
};

const mapStatetoProps = (state) => {
	const { user, token } = state.Login;
	const { App } = state;
	const organization = state.Organization;
    const { 
		Modules: modules 
	} = state;

	return {
		token,
		user,
		App,
		organization,
        modules
	};
};

const mapDispachToProps = (dispatch) => {
	return {
		apiError: (error) => dispatch(apiError(error)),
		appLoadStatusChanged: (status) => dispatch(appLoadStatusChanged(status)),
		errorNotification: (message) => dispatch(errorNotification(message)),
		changePreloader: (val) => dispatch(changePreloader(val)),
		changeWorkspace: (workspace) => dispatch(changeWorkspace(workspace)),
		loginUserSuccessful: (user) => dispatch(loginUserSuccessful(user)),
		handleGetInfoOrganizationSuccess: (val) => dispatch(onGetInfoOrganizationSuccess(val)),
	};
};

export default withNamespaces()(
	withRouter(
		connect(mapStatetoProps, mapDispachToProps)(Dashboard)
	)
)